import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';

import { useTextBackgroundData } from './hooks';
import { SPACING } from './constants';

import { RichText } from 'components/Prismic';
import LineClamp from 'components/LineClamp';
import { Normalize } from 'utils/types';
import { filterProps } from 'utils/helpers';

export type TTextBackgroundSection =
  Normalize<Queries.SolutionDesktopTextBackgroundData_PrismicSolutionDataDesktopSolutionTextBackground_Fragment>;

const rowSpacing = {
  [SPACING.LOW]: '3.25rem',
  [SPACING.HIGH]: '15rem',
};

const Container = styled('div', filterProps('spacing'))<{
  spacing: SPACING;
}>`
  ${({ theme, spacing }) => css`
    display: grid;
    position: relative;
    row-gap: ${rowSpacing[spacing]};
    align-content: center;
    margin-bottom: -1px;
    height: 139.625rem;

    ${theme.mq.md} {
      padding: 7rem ${theme.spacings.padding};
      height: initial;

      && {
        display: grid;
      }
    }
  `}
`;

const BackgroundImage = styled(GatsbyImage)`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const TitleContainer = styled.div`
  ${({ theme: { mq } }) => css`
    display: flex;
    flex-direction: column;
    row-gap: 1rem;
    z-index: 1;
    margin-left: 12.5rem;

    ${mq.md} {
      row-gap: 0.5rem;
      margin-left: 0;
    }
  `}
`;

const Title = styled(RichText)`
  ${({ theme: { colors, mq, tp } }) => css`
    display: flex;

    & > h2 {
      background-color: #9a9a9a;
      padding-right: 1.5rem;
      text-align: left;
      line-height: 1.04;
      letter-spacing: -0.1em;
      color: ${colors.black};
      font-size: 12.25rem;

      ${tp.useFont(fonts => fonts.Switzer.lightest)};
    }

    ${mq.md} {
      & > h2 {
        font-size: 4rem;
      }
    }
  `}
`;

const DescriptionContainer = styled.div`
  ${({ theme: { mq } }) => css`
    z-index: 1;
    margin-left: 10rem;
    width: fit-content;

    ${mq.md} {
      margin-left: 0;
      width: initial;
    }
  `}
`;

const Description = styled(RichText)`
  ${({ theme: { colors, mq, tp } }) => css`
    display: flex;
    background-color: ${colors.black}59;

    & > p {
      padding: 1.5rem 2rem;
      text-align: left;
      line-height: 1.2;
      color: ${colors.white};
      font-size: 3.25rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};
    }

    ${mq.md} {
      background-color: initial;

      & > p {
        padding: 0;
        font-size: 2rem;

        br {
          display: none;
        }
      }
    }
  `}
`;

const TextBackgroundSection: React.FC<TTextBackgroundSection> = sliceData => {
  const { backgroundImage, title, description, spacing } = useTextBackgroundData(sliceData);

  return (
    <Container spacing={spacing}>
      {backgroundImage ? <BackgroundImage {...backgroundImage} /> : null}
      <TitleContainer>
        {title.map(titleText => (
          <Title key={JSON.stringify(titleText)} render={titleText} />
        ))}
      </TitleContainer>
      <DescriptionContainer>
        <LineClamp>
          <Description render={description} />
        </LineClamp>
      </DescriptionContainer>
    </Container>
  );
};

export const desktopTextBackgroundFragment = graphql`
  fragment SolutionDesktopTextBackgroundData on PrismicSolutionDataDesktopSlicesType {
    ... on PrismicSolutionDataDesktopSolutionTextBackground {
      slice_type

      primary {
        solution_text_background_image {
          gatsbyImageData
          alt
        }
        solution_text_background_title {
          richText
        }
        solution_text_background_description {
          richText
        }
        solution_text_background_spacing
      }
    }
  }
`;

export default TextBackgroundSection;
